import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import chunk from 'lodash/chunk';
import { useTheme } from '@mui/material';
import { Stats } from '../../../utils/statistics';
import { VarName, varNameDetails } from '../../../utils/varNames';
import {
  CalendarSelectionType,
  HeatmapHoverSelection,
  HoveredPlot,
  PlotType,
  ValueType,
} from '../../CalendarView/helpers';
import { useAppSelector } from '../../../state/store';
import {
  getAvgPerColumnData,
  getColorScaleValue,
  getCsvData,
  getHighlightedItems,
  getHourValues,
  getMainPlotData,
  getMinMaxAvgPerColumnData,
  getMinMaxAvgPerDayData,
  getSubPlotResult,
  getWeekPlotLayout,
  getZmaxValue,
} from './calendarViewPlotHelpers';
import useStyles from '../../../styles/calendarView';
import {
  getSelectedEndDate,
  getSelectedHours,
  getSelectedStartDate,
} from '../../../state/selectors';
import { varNameBandParams } from '../../../utils/dataBandParams';
import Plot from '../PlotlyCustom';
import { TimeRange } from '../plotCommon';

dayjs.extend(isSameOrBefore);

interface HourlyRawViewPlotProps {
  combinedSensorStats: Stats;
  varName: VarName;
  valueType: ValueType | undefined;
  showGradient?: boolean;
  setHoveredPlot?: ((value: HeatmapHoverSelection | undefined) => void) | undefined;
  downloadData: boolean;
  setDownloadData: (value: boolean) => void;
  sourceLabel: string;
}

function getDatesInRange(start: Date | string, end: Date | string): Date[] {
  const dates: Date[] = [];
  let current = dayjs(start).startOf('day');
  const last = dayjs(end).startOf('day');
  while (current.isSameOrBefore(last, 'day')) {
    dates.push(current.toDate());
    current = current.add(1, 'day');
  }
  return dates;
}

function HourlyRawViewPlot({
  combinedSensorStats,
  varName,
  valueType,
  showGradient,
  setHoveredPlot,
  downloadData,
  setDownloadData,
  sourceLabel,
}: HourlyRawViewPlotProps): JSX.Element | null {
  const { classes } = useStyles();
  const theme = useTheme();
  const selectedHours = useAppSelector(getSelectedHours);
  const startDate = useAppSelector(getSelectedStartDate);
  const endDate = useAppSelector(getSelectedEndDate);

  const isTot = combinedSensorStats.totalAdded !== undefined;
  const isUtl = combinedSensorStats.utl !== undefined;

  // x-axis (horizontal) = every actual date in the selected range
  const mainPlotXDates = useMemo(() => getDatesInRange(startDate, endDate), [startDate, endDate]);

  // y-axis (vertical) = hours of day, same as WeekViewPlot's x-axis was
  const mainPlotYHourOfDay = useMemo(() => getHourValues(selectedHours), [selectedHours]);

  const highlightTimes: TimeRange[] = useMemo(
    () =>
      getHighlightedItems(
        mainPlotXDates,
        !selectedHours.selectHours || selectedHours.includeWeekends
      ),
    [mainPlotXDates, selectedHours]
  );

  const initialMainPlotZvalues = useMemo(() => {
    const zValues: number[] = [];
    // Outer loop = rows (hours), inner loop = columns (dates), so the
    // flattened array chunks correctly into z[row=hour][col=date].
    mainPlotYHourOfDay.forEach((hour) => {
      mainPlotXDates.forEach((date) => {
        const dateKey = dayjs(date).format('YYYY-MM-DD');
        const stats = combinedSensorStats.heatMapStats?.byDateAndHour?.get(dateKey)?.get(hour);
        if (stats === undefined) {
          zValues.push(-Infinity);
        } else if (isTot) {
          zValues.push(stats.totalAdded ?? -Infinity);
        } else if (isUtl) {
          const utlPct = stats.utl === undefined ? -Infinity : stats.utl * 100;
          zValues.push(utlPct);
        } else if (valueType === ValueType.min) {
          zValues.push(stats.maxMinAvg.min);
        } else if (valueType === ValueType.max) {
          zValues.push(stats.maxMinAvg.max);
        } else {
          zValues.push(Math.round(stats.maxMinAvg.mean * 10) / 10);
        }
      });
    });
    return zValues;
  }, [mainPlotXDates, mainPlotYHourOfDay, combinedSensorStats, valueType, isTot, isUtl]);

  const zmax = useMemo(() => {
    const maxData = isTot ? Math.max(...initialMainPlotZvalues) : combinedSensorStats.maxMinAvg.max;
    return getZmaxValue([maxData], varName);
  }, [combinedSensorStats, varName, isTot, initialMainPlotZvalues]);

  const zmin = useMemo(() => {
    if (isTot) return 0;
    if (isUtl) return 0;
    const bands = varNameBandParams[varName];
    const bandMinValue = bands ? bands[0]?.upperBound : 0;
    return Math.min(bandMinValue, combinedSensorStats.maxMinAvg.min);
  }, [isTot, isUtl, combinedSensorStats, varName]);

  const colorscaleValue = useMemo(
    () => getColorScaleValue(varName, zmin, zmax, showGradient),
    [varName, zmin, zmax, showGradient]
  );

  const mainPlotZValues = useMemo(() => {
    let zValueData;
    if (mainPlotXDates) {
      const shapedData = chunk(initialMainPlotZvalues, mainPlotXDates.length);
      if (shapedData) zValueData = shapedData;
    }
    return zValueData;
  }, [initialMainPlotZvalues, mainPlotXDates]);

  // Full min/max/avg breakdown per HOUR (mirrors WeekViewPlot's per-day
  // subplot, but now hours are the axis with many categories, so they get
  // the full stats treatment; positioned beside the main plot, x2/y).
  const minMaxAvgPerHour = useMemo(() => {
    const zValues: number[] = [];
    mainPlotYHourOfDay.forEach((hour) => {
      const stats = combinedSensorStats.heatMapStats?.byHourOfDay.get(hour);
      if (stats === undefined) {
        zValues.push(-Infinity, -Infinity, -Infinity);
      } else if (isTot) {
        zValues.push(
          stats.totalAdded ?? -Infinity,
          stats.totalAdded ?? -Infinity,
          stats.totalAdded ?? -Infinity
        );
      } else if (isUtl) {
        const utlPct = stats.utl === undefined ? -Infinity : stats.utl * 100;
        zValues.push(utlPct, utlPct, utlPct);
      } else {
        zValues.push(
          Number.isNaN(stats.maxMinAvg.min) ? -Infinity : stats.maxMinAvg.min,
          Number.isNaN(stats.maxMinAvg.mean)
            ? -Infinity
            : Math.round(stats.maxMinAvg.mean * 10) / 10,
          Number.isNaN(stats.maxMinAvg.max) ? -Infinity : stats.maxMinAvg.max
        );
      }
    });
    return chunk(zValues, 3);
  }, [mainPlotYHourOfDay, combinedSensorStats, isTot, isUtl]);

  // Avg-only per DATE (mirrors WeekViewPlot's "avg per hour column" subplot,
  // positioned below the main plot, x/y2). Depends on the assumed `byDate`
  // map — see the header note above.
  const avgPerDate = useMemo(() => {
    const zValues: number[] = [];
    mainPlotXDates.forEach((date) => {
      const dateKey = dayjs(date).format('YYYY-MM-DD');
      const stats = combinedSensorStats.heatMapStats?.byDate?.get(dateKey);
      if (stats === undefined) {
        zValues.push(-Infinity);
      } else if (isTot) {
        zValues.push(stats.totalAdded ?? -Infinity);
      } else if (isUtl) {
        const utlPct = stats.utl === undefined ? -Infinity : stats.utl * 100;
        zValues.push(utlPct);
      } else {
        zValues.push(
          Number.isNaN(stats.maxMinAvg.mean)
            ? -Infinity
            : Math.round(stats.maxMinAvg.mean * 10) / 10
        );
      }
    });
    return [zValues];
  }, [mainPlotXDates, combinedSensorStats, isTot, isUtl]);

  // Corner subplot: overall min/max/avg across the whole selection - same as
  // WeekViewPlot, unaffected by the axis flip.
  const minMaxAvgPerColumn = useMemo(() => {
    const stats = combinedSensorStats;
    const zValues: number[] = [];
    if (stats === undefined) {
      zValues.push(-Infinity, -Infinity, -Infinity);
    } else if (isTot) {
      zValues.push(
        stats.totalAdded ?? -Infinity,
        stats.totalAdded ?? -Infinity,
        stats.totalAdded ?? -Infinity
      );
    } else if (isUtl) {
      const utlPct = stats.utl === undefined ? -Infinity : stats.utl * 100;
      zValues.push(utlPct, utlPct, utlPct);
    } else {
      zValues.push(
        Number.isNaN(stats.maxMinAvg.min) ? -Infinity : stats.maxMinAvg.min,
        Number.isNaN(stats.maxMinAvg.mean) ? -Infinity : Math.round(stats.maxMinAvg.mean * 10) / 10,
        Number.isNaN(stats.maxMinAvg.max) ? -Infinity : stats.maxMinAvg.max
      );
    }
    return [zValues];
  }, [combinedSensorStats, isTot, isUtl]);

  const mainPlotData = useMemo(
    () =>
      getMainPlotData(
        colorscaleValue,
        mainPlotXDates,
        mainPlotYHourOfDay,
        mainPlotZValues,
        zmin,
        zmax,
        isTot,
        varNameDetails[varName].metric ?? '',
        true
      ),
    [
      colorscaleValue,
      isTot,
      mainPlotXDates,
      mainPlotYHourOfDay,
      mainPlotZValues,
      varName,
      zmax,
      zmin,
    ]
  );

  const minMaxAvgPerHourData = useMemo(
    () =>
      getMinMaxAvgPerDayData(
        colorscaleValue,
        minMaxAvgPerHour,
        mainPlotYHourOfDay,
        zmin,
        zmax,
        isTot,
        varNameDetails[varName].metric,
        0.4
      ),
    [colorscaleValue, isTot, mainPlotYHourOfDay, minMaxAvgPerHour, varName, zmax, zmin]
  );

  const avgPerDateData = useMemo(
    () =>
      getAvgPerColumnData(
        colorscaleValue,
        avgPerDate,
        mainPlotXDates,
        zmin,
        zmax,
        isTot,
        varNameDetails[varName].metric
      ),
    [avgPerDate, colorscaleValue, isTot, mainPlotXDates, varName, zmax, zmin]
  );

  const minMaxAvgPerColumnData = useMemo(
    () =>
      getMinMaxAvgPerColumnData(
        colorscaleValue,
        minMaxAvgPerColumn,
        zmin,
        zmax,
        isTot,
        varNameDetails[varName].metric
      ),
    [colorscaleValue, isTot, minMaxAvgPerColumn, varName, zmax, zmin]
  );

  const subPlots = useMemo(() => {
    let plots = [];
    if (isTot) {
      plots = [
        { x: [PlotType.tot], y: mainPlotYHourOfDay, z: minMaxAvgPerHour, xRef: 'x2', yRef: 'y' },
        { x: mainPlotXDates, y: [PlotType.tot], z: avgPerDate, xRef: 'x', yRef: 'y2' },
        { x: [PlotType.tot], y: [PlotType.tot], z: minMaxAvgPerColumn, xRef: 'x2', yRef: 'y2' },
      ];
    } else {
      plots = [
        {
          x: [PlotType.min, PlotType.avg, PlotType.max],
          y: mainPlotYHourOfDay,
          z: minMaxAvgPerHour,
          xRef: 'x2',
          yRef: 'y',
        },
        { x: mainPlotXDates, y: [PlotType.avg], z: avgPerDate, xRef: 'x', yRef: 'y2' },
        {
          x: [PlotType.min, PlotType.avg, PlotType.max],
          y: [PlotType.avg],
          z: minMaxAvgPerColumn,
          xRef: 'x2',
          yRef: 'y2',
        },
      ];
    }
    return plots;
  }, [avgPerDate, isTot, mainPlotXDates, mainPlotYHourOfDay, minMaxAvgPerColumn, minMaxAvgPerHour]);

  const layout = useMemo(() => {
    let plotlyLayout;
    if (mainPlotXDates && mainPlotYHourOfDay && mainPlotZValues && zmin !== undefined) {
      plotlyLayout = getWeekPlotLayout(
        mainPlotXDates,
        mainPlotYHourOfDay,
        mainPlotZValues,
        valueType,
        highlightTimes,
        theme,
        true
      );
    }
    for (let i = 0; i < subPlots.length; i++) {
      const { x, y, z, xRef, yRef } = subPlots[i];
      const subPlotResult = getSubPlotResult(x, y, z, xRef, yRef, false, valueType, false);
      if (subPlotResult && plotlyLayout?.annotations) {
        const updatedLayout = plotlyLayout?.annotations.concat(subPlotResult);
        plotlyLayout.annotations = updatedLayout;
      }
    }
    return plotlyLayout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zmin, valueType, mainPlotZValues, highlightTimes]);

  const handleHover = (plotDatum: HoveredPlot | undefined) => {
    if (setHoveredPlot === undefined) return;
    if (plotDatum === undefined) {
      setHoveredPlot(undefined);
      return;
    }
    const filter: HeatmapHoverSelection = { hoverValue: plotDatum.z as number };
    const summaryTypes = [PlotType.min, PlotType.avg, PlotType.max, PlotType.tot];
    const xSummary = summaryTypes.find((t) => t === plotDatum.x);
    const ySummary = summaryTypes.find((t) => t === plotDatum.y);

    // x is a date (main plot, bottom strip) or a stat label (right strip, corner)
    if (xSummary) filter.summaryType = xSummary;
    else filter.date = dayjs(plotDatum.x as string).toDate();

    // y is an hour (main plot, right strip) or a stat label (bottom strip, corner)
    if (typeof plotDatum.y === 'number') filter.hour = plotDatum.y;
    else if (ySummary && !filter.summaryType) filter.summaryType = ySummary;

    setHoveredPlot(filter);
  };

  const hasAllPlotData =
    mainPlotData && minMaxAvgPerHourData && avgPerDateData && minMaxAvgPerColumnData;

  const handleDownload = () => {
    setDownloadData(false);
    const csvData = getCsvData(
      zmin,
      mainPlotXDates,
      mainPlotYHourOfDay,
      mainPlotZValues,
      minMaxAvgPerHour,
      avgPerDate,
      minMaxAvgPerColumn,
      CalendarSelectionType.week,
      true
    );
    if (csvData) {
      const csvFile = new Blob([csvData], { type: 'text/csv' });
      const downloadLink = document.createElement('a');
      downloadLink.download = `${sourceLabel}-${varName}-dayview-${dayjs(startDate).format(
        'LL'
      )}-${dayjs(endDate).format('LL')}.csv`;
      downloadLink.href = window.URL.createObjectURL(csvFile);
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
    }
    return null;
  };

  if (hasAllPlotData && layout) {
    return (
      <>
        {downloadData && handleDownload()}
        <Plot
          data={[mainPlotData, minMaxAvgPerHourData, avgPerDateData, minMaxAvgPerColumnData]}
          layout={layout}
          className={classes.plotContainer}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onHover={(e) => handleHover({ x: e.points[0].x, y: e.points[0].y, z: e.points[0].z })}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onUnhover={() => handleHover(undefined)}
          config={{ displayModeBar: false, showAxisDragHandles: false }}
          useResizeHandler
        />
      </>
    );
  }
  return null;
}

HourlyRawViewPlot.defaultProps = {
  showGradient: true,
  setHoveredPlot: undefined,
};

export default HourlyRawViewPlot;
